/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import user from 'js/lib/user';

import { Link, Typography, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { SectionName, UpsellType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import UpsellCard from 'bundles/unified-description-page-common/components/modules/upsell/UpsellCard';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';

import upsellModuleC4B from 'images/unified-description-page/mweb-xdp-mobile-c4b-upsell.png';
import upsellModuleEnterprise from 'images/unified-description-page/upsell-module-enterprise.png';

import _t from 'i18n!nls/xdp';

type Props = {
  // This will be added dynamically from UpsellModule.
  amountOfCards?: number;
};

const styles = {
  cta: () => css`
    color: var(--cds-color-blue-700);
  `,
};

const EnterpriseUpsellCard = ({ amountOfCards }: Props) => {
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();
  const isMobileSubscriptionFirst = useMediaQuery(breakpoints.down('sm')) && shouldShowXdpChanges;
  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const trackingData = {
    upsellType: 'enterprise' as UpsellType,
    pageSection: {
      sectionName: 'upsell' as SectionName,
    },
    ...eventingProductData,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_upsell_card',
    trackingData
  );

  return (
    <UpsellCard
      title={
        <Typography variant="h1semibold" component="h2">
          {_t('Join over 3,400 global companies that choose Coursera for Business')}
        </Typography>
      }
      description={
        <Typography2 variant="bodySecondary" component="p">
          {_t('Upskill your employees to excel in the digital economy')}
        </Typography2>
      }
      cta={
        <Link
          component={TrackedA}
          href={`/business?utm_medium=coursera&utm_source=xdp&utm_campaign=website&utm_content=c4b-xdp-upsell-card&utm_term=${
            user.isAuthenticatedUser() ? 'in' : 'out'
          }`}
          trackingName="upsell_cta_enterprise"
          typographyVariant="subtitleMedium"
          variant="quiet"
          css={styles.cta}
          aria-label={_t('Learn more about Coursera for Business')}
          icon={<ArrowNextIcon />}
          onClick={() => {
            track('click_upsell_card', trackingData);
          }}
        >
          {_t('Learn more')}
        </Link>
      }
      backgroundImage={isMobileSubscriptionFirst ? upsellModuleC4B : upsellModuleEnterprise}
      backgroundPosition="90%"
      backgroundColor={isMobileSubscriptionFirst ? 'var(--cds-color-grey-100)' : undefined}
      amountOfCards={amountOfCards}
      ref={visibilityTrackingRef}
    />
  );
};

export default EnterpriseUpsellCard;
